import { updateIDs } from 'app/copied/getIds';
import { IWebChatModule, ostroAdapter } from 'app/services/ostroAdapter';
import queryClient from 'app/services/queryClient';
import { findFirstMatchingLanguage, getLanguageCodefromUrl } from 'app/utils/language';
import i18n from 'i18next';
import { useEffect, useMemo } from 'react';

import { Language } from '../i18n/translationModels';
import useNamespacedLocalStorage from './useNamespacedLocalStorage';

export interface ILanguageConfig {
  showLanguageSelector: boolean;
  webchatLanguageModules: IWebChatModule[];
  selectedLanguageCondition: string;
}

export function useLanguages(): ILanguageConfig {
  const [selectedLanguageCondition, setLanguageCondition] = useNamespacedLocalStorage('ostroLanguageCondition', '');

  let showLanguageSelector = false;

  const webchatLanguageModules = useMemo(() => {
    return ostroAdapter.getWebchatModules();
  }, []); //empty array to avoid re-rendering, ostro.js loads webchats only once

  if (webchatLanguageModules.length > 1) {
    showLanguageSelector = webchatLanguageModules.filter((module: IWebChatModule) => module.enabled).length > 1;
  }

  useEffect(() => {
    const urlLanguageCode = getLanguageCodefromUrl();
    let selectedLanguageModule;

    const refreshIDs = async () => {
      await updateIDs();
      await queryClient.invalidateQueries();
    };

    // Priority 1: Check if 'tailor-lang' parameter is in the URL and set language accordingly
    if (urlLanguageCode) {
      const matchingModule = webchatLanguageModules.find(
        (module: IWebChatModule) => module.enabled && module.language_codes.includes(urlLanguageCode)
      );

      if (matchingModule) {
        i18n.changeLanguage(urlLanguageCode);
        setLanguageCondition(matchingModule.condition_uuid);
        refreshIDs();
        return;
      }
    }

    // Priority 2: Use previously selected language condition or default language
    if (!selectedLanguageCondition) {
      selectedLanguageModule = webchatLanguageModules.find(
        (module: IWebChatModule) => module.enabled && module.language_codes.includes('default')
      );
      selectedLanguageModule && setLanguageCondition(selectedLanguageModule.condition_uuid);
    } else {
      selectedLanguageModule = webchatLanguageModules.find(
        (module: IWebChatModule) => module.enabled && module.condition_uuid === selectedLanguageCondition
      );
    }

    i18n.changeLanguage(findFirstMatchingLanguage(selectedLanguageModule?.language_codes ?? []) || Language.English);
  }, [selectedLanguageCondition, setLanguageCondition, webchatLanguageModules]);

  return {
    showLanguageSelector,
    webchatLanguageModules,
    selectedLanguageCondition,
  };
}
